import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import type { CartItem as CartItemType } from "@/types";
import { formatCurrency, cn } from "@/lib/utils";
import { cartItemVariants, springTransition } from "@/lib/animations";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity?: (productId: string, quantity: number, variant?: string, externalItemId?: number) => Promise<void>;
  onRemove?: (productId: string, externalItemId?: number) => Promise<void>;
  isUpdating?: boolean;
  isMarketplace: boolean;
  compact?: boolean;
  isFreeItem?: boolean | number; // true = fully free, number = discounted total cents
  stockMessage?: string;
}

// Placeholder image component for products without valid images
function ProductPlaceholder({ className = "w-6 h-6" }: { className?: string }) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-[var(--color-surface-secondary)]">
      <svg
        className={cn(className, "text-(--color-text-placeholder)")}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    </div>
  );
}

export function CartItem({
  item,
  onUpdateQuantity,
  onRemove,
  isUpdating = false,
  isMarketplace,
  compact = false,
  isFreeItem = false,
  stockMessage,
}: CartItemProps) {
  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const [isRemoving, setIsRemoving] = useState(false);
  const [imageError, setImageError] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    setLocalQuantity(item.quantity);
  }, [item.quantity]);

  // Determine which image to display: variant image if selected, otherwise product image
  const displayImage = item.variant_image && item.variant_image.trim() !== ""
    ? item.variant_image
    : item.image_url;


  // Reset image error when image_url changes
  const handleImageError = () => setImageError(true);

  const handleQuantityChange = async (newQuantity: number) => {
    if (newQuantity < 1) return;
    setLocalQuantity(newQuantity);
    await onUpdateQuantity?.(item.product_id, newQuantity, item.selected_variant ? item.sku : undefined, item.external_item_id ? Number(item.external_item_id) : undefined);
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      await onRemove?.(
        item.product_id,
        item.external_item_id ? Number(item.external_item_id) : undefined,
      );
    } catch (error) {
      setIsRemoving(false);
    }
  };

  const MotionWrapper = prefersReducedMotion ? "div" : motion.div;
  const motionProps = prefersReducedMotion
    ? {}
    : {
        layout: true,
        initial: "hidden",
        animate: "visible",
        exit: "exit",
        variants: cartItemVariants,
      };

  if (compact) {
    return (
      <MotionWrapper {...motionProps}>
        <div className="flex items-center gap-4 py-2">
          <motion.div
            className="product-thumbnail"
            whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            transition={springTransition}
          >
            {displayImage  && !imageError ? (
              <img
                src={displayImage }
                alt={item.title}
                onError={handleImageError}
                className="w-full h-full object-cover"
              />
            ) : (
              <ProductPlaceholder className="w-6 h-6" />
            )}
            <AnimatePresence>
              {item.quantity > 1 && (
                <motion.span
                  className="product-thumbnail-badge"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  transition={springTransition}
                >
                  {item.quantity}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] text-(--color-text-primary) truncate">{item.title}</p>
            {isMarketplace && (
              <p className="text-[13px] text-(--color-text-secondary) mt-0.5">
                Brand: {item.fulfillment_brand_name}
              </p>
            )}
            {item.selected_variant && (
              <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                Variant: {item.selected_variant}
              </p>
            )}
            <p className="text-[12px] text-(--color-text-muted) mt-0.5">
              Quantity: {item.quantity}
            </p>
            {!item.in_stock && (
              <p className="text-[12px] text-(--color-error) mt-0.5">Out of stock</p>
            )}
            {stockMessage && (
              <p className="text-[12px] text-(--color-error) mt-0.5">
                ({stockMessage})
              </p>
            )}
          </div>
          <motion.div
            className={cn(
              "text-[14px] font-medium tabular-nums",
              isFreeItem ? "text-[var(--color-success)] line-through" : "text-(--color-text-primary)"
            )}
            key={item.line_total_cents}
            initial={prefersReducedMotion ? {} : { scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={springTransition}
          >
            {formatCurrency(item.line_total_cents)}
          </motion.div>
          {(item.discount_cents ?? 0) > 0 && (
            <p className="text-[11px] text-(--color-success) text-right">
              Discount allocation: {formatCurrency(item.discount_cents!)}
            </p>
          )}
          {isFreeItem === true && (
            <p className="text-[12px] text-(--color-text-primary) font-medium mt-0.5">Free</p>
          )}
          {typeof isFreeItem === "number" && (
            <p className="text-[12px] text-(--color-text-primary) font-medium tabular-nums mt-0.5">
              {formatCurrency(isFreeItem)}
            </p>
          )}
        </div>
      </MotionWrapper>
    );
  }

  return (
    <MotionWrapper {...motionProps}>
      <div className="flex gap-4 py-3 border-b border-(--color-surface-border-light) last:border-0">
        {/* Product Image */}
        <motion.div
          className="product-thumbnail-long"
          whileHover={prefersReducedMotion ? {} : { scale: 1.03 }}
          transition={springTransition}
        >
          {displayImage  && !imageError ? (
            <img
              src={displayImage }
              alt={item.title}
              onError={handleImageError}
              className="w-full h-full object-cover"
            />
          ) : (
            <ProductPlaceholder className="w-8 h-8" />
          )}
        </motion.div>

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-[14px] font-medium text-(--color-text-primary) truncate">
                {item.title}
              </h3>
              {isMarketplace && (
                <p className="text-[13px] text-(--color-text-secondary) mt-0.5">
                  Brand: {item.fulfillment_brand_name}
                </p>
              )}
              {item.selected_variant && (
                <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                  Variant: {item.selected_variant}
                </p>
              )}
              <p className="text-[12px] text-(--color-text-muted) mt-0.5">SKU: {item.sku}</p>
              {!item.in_stock && (
                <motion.p
                  className="text-[12px] text-(--color-error) mt-1 font-medium"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  Out of stock
                </motion.p>
              )}
              {stockMessage && (
                <motion.p
                  className="text-[12px] text-(--color-error) mt-1 font-medium"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  ({stockMessage})
                </motion.p>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <motion.p
                className={cn(
                  "text-[14px] font-medium tabular-nums",
                  isFreeItem ? "text-[var(--color-success)] line-through" : "text-(--color-text-primary)"
                )}
                key={item.line_total_cents}
                initial={prefersReducedMotion ? {} : { scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={springTransition}
              >
                {formatCurrency(item.line_total_cents)}
              </motion.p>
              {(item.discount_cents ?? 0) > 0 && (
                <p className="text-[11px] text-(--color-success) text-right">
                  Discount allocation: {formatCurrency(item.discount_cents!)}
                </p>
              )}
              {isFreeItem === true && (
                <p className="text-[12px] text-(--color-text-primary) font-medium mt-0.5">Free</p>
              )}
              {typeof isFreeItem === "number" && (
                <p className="text-[12px] text-(--color-text-primary) font-medium tabular-nums mt-0.5">
                  {formatCurrency(isFreeItem)}
                </p>
              )}
              <AnimatePresence>
                {item.quantity > 1 && !isFreeItem && (
                  <motion.p
                    className="text-[12px] text-(--color-text-muted) mt-0.5 tabular-nums"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {formatCurrency(item.unit_price_cents)} each
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3">
            {/* Quantity Controls */}
            <div className="flex items-center border border-[var(--color-surface-border)] rounded-lg overflow-hidden">
              <motion.button
                onClick={() => handleQuantityChange(localQuantity - 1)}
                disabled={isUpdating || localQuantity <= 1}
                className={cn(
                  "w-8 h-8 flex items-center justify-center",
                  "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-[var(--color-bg-hover)]",
                  "transition-colors",
                  "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                )}
                aria-label="Decrease quantity"
                whileTap={prefersReducedMotion ? {} : { scale: 0.9 }}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
              </motion.button>
              <motion.span
                className="w-10 h-8 flex items-center justify-center text-[14px] font-medium text-(--color-text-primary) tabular-nums border-x border-[var(--color-surface-border)]"
                key={localQuantity}
                initial={prefersReducedMotion ? {} : { scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={springTransition}
              >
                {localQuantity}
              </motion.span>
              <motion.button
                onClick={() => handleQuantityChange(localQuantity + 1)}
                disabled={isUpdating || !item.in_stock}
                className={cn(
                  "w-8 h-8 flex items-center justify-center",
                  "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-[var(--color-bg-hover)]",
                  "transition-colors",
                  "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                )}
                aria-label="Increase quantity"
                whileTap={prefersReducedMotion ? {} : { scale: 0.9 }}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </motion.button>
            </div>

            {/* Remove Button */}
            <motion.button
              onClick={handleRemove}
              disabled={isUpdating || isRemoving}
              className={cn(
                "text-[13px] text-(--color-text-muted) hover:text-(--color-error)",
                "transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "inline-flex items-center gap-1.5"
              )}
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              {isRemoving ? (
                <LoadingSpinner size="sm" className="text-current" />
              ) : null}
              {isRemoving ? "Removing..." : "Remove"}
            </motion.button>
          </div>
        </div>
      </div>
    </MotionWrapper>
  );
}
